'use client';

import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { NrfIconInBox } from '~/components/nrf-icon-in-box';
import { NrfMaterialIcon } from '~/components/nrf-material-icon';

export function MainFeatureOverview() {
  return (
    <div className="bg-stone-100 py-16">
      <div className={'container mx-auto'}>
        <div
          className={
            'grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-4 lg:grid-cols-3 xl:gap-6'
          }
        >
          {/* Col 1 */}
          <FeatureBox
            icon={
              <NrfIconInBox
                icon={
                  <NrfMaterialIcon
                    materialName="content_cut "
                    className="text-4.5xl md:text-5xl"
                  />
                }
                iconBoxClassName={
                  'bg-primary/20 h-[60px] w-[60px] md:h-[72px] md:w-[72px]'
                }
              ></NrfIconInBox>
            }
            headline={
              <Trans
                i18nKey={'marketing:sectionOverview:autoEditingHeadline'}
              />
            }
            text={
              <Trans i18nKey={'marketing:sectionOverview:autoEditingText'} />
            }
            className={'p-6 lg:p-8 xl:p-10'}
          ></FeatureBox>

          {/* Col 2 */}
          <FeatureBox
            icon={
              <NrfIconInBox
                icon={
                  <NrfMaterialIcon
                    materialName="spellcheck"
                    className="text-5xl text-red-500 md:text-5.5xl"
                    weight={600}
                  />
                }
                iconBoxClassName={
                  'bg-red-500/20  h-[60px] w-[60px] md:h-[72px] md:w-[72px]'
                }
              ></NrfIconInBox>
            }
            headline={
              <Trans
                i18nKey={'marketing:sectionOverview:autoProofingHeadline'}
              />
            }
            text={
              <Trans i18nKey={'marketing:sectionOverview:autoProofingText'} />
            }
            className={'p-6 lg:p-8 xl:p-10'}
          ></FeatureBox>

          {/* Col 3 */}
          <FeatureBox
            icon={
              <NrfIconInBox
                icon={
                  <NrfMaterialIcon
                    materialName="language"
                    className="text-5xl text-blue-600 md:text-5.5xl"
                  />
                }
                iconBoxClassName={
                  'bg-blue-600/20  h-[60px] w-[60px] md:h-[72px] md:w-[72px]'
                }
              ></NrfIconInBox>
            }
            headline={
              <Trans i18nKey={'marketing:sectionOverview:multilangHeadline'} />
            }
            text={<Trans i18nKey={'marketing:sectionOverview:multilangText'} />}
            className={'p-6 lg:p-8 xl:p-10'}
          ></FeatureBox>

          {/* Col 4 */}
          <FeatureBox
            icon={
              <NrfIconInBox
                icon={
                  <NrfMaterialIcon
                    materialName="security"
                    className="text-4.5xl text-lime-600 md:text-5xl"
                  />
                }
                iconBoxClassName={
                  'bg-lime-600/20  h-[60px] w-[60px] md:h-[72px] md:w-[72px]'
                }
              ></NrfIconInBox>
            }
            headline={
              <Trans i18nKey={'marketing:sectionOverview:securityHeadline'} />
            }
            text={<Trans i18nKey={'marketing:sectionOverview:securityText'} />}
            className={'p-6 lg:p-8 xl:p-10'}
          ></FeatureBox>

          {/* Col 5 */}
          <FeatureBox
            icon={
              <NrfIconInBox
                icon={
                  <NrfMaterialIcon
                    materialName="download"
                    className="text-5xl text-yellow-500 md:text-5.5xl"
                  />
                }
                iconBoxClassName={
                  'bg-yellow-500/20  h-[60px] w-[60px] md:h-[72px] md:w-[72px]'
                }
              ></NrfIconInBox>
            }
            headline={
              <Trans i18nKey={'marketing:sectionOverview:dawSupportHeadline'} />
            }
            text={
              <Trans i18nKey={'marketing:sectionOverview:dawSupportText'} />
            }
            className={'p-6 lg:p-8 xl:p-10'}
          ></FeatureBox>

          {/* Col 6 */}
          <FeatureBox
            icon={
              <NrfIconInBox
                icon={
                  <NrfMaterialIcon
                    materialName="apartment"
                    className="text-4.5xl md:text-5xl"
                  />
                }
                iconBoxClassName={
                  'bg-primary/20  h-[60px] w-[60px] md:h-[72px] md:w-[72px]'
                }
              ></NrfIconInBox>
            }
            headline={
              <Trans
                i18nKey={'marketing:sectionOverview:apiIntegrationHeadline'}
              />
            }
            text={
              <Trans i18nKey={'marketing:sectionOverview:apiIntegrationText'} />
            }
            className={'p-6 lg:p-8 xl:p-10'}
          ></FeatureBox>
        </div>
      </div>
    </div>
  );
}

interface FeatureBoxProps {
  icon: React.ReactNode;
  headline?: React.ReactNode;
  text: React.ReactNode;
  className?: string;
}

function FeatureBox({ icon, headline, text, className }: FeatureBoxProps) {
  return (
    <div
      className={cn(
        'flex grow flex-col space-y-4 rounded-3xl bg-white lg:p-10',
        className,
      )}
    >
      {icon}
      <h3 className="font-heading text-2xl lg:text-3xl">{headline}</h3>
      <p>{text}</p>
    </div>
  );
}
